import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'store';
import { isCallActiveStatus } from 'store/slices/twilioOutboundCall';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
    Avatar,
    Box,
    Chip,
    ClickAwayListener,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Paper,
    Popper,
    Stack,
    Typography
} from '@mui/material';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import useAuth from 'hooks/useAuth';
import User1 from 'assets/images/users/user-round.svg';
import logger from 'utils/logger';
import { fetchAccountOrganizations } from 'api/account';

// assets
import { IconLogout, IconSettings, IconSwitchHorizontal } from '@tabler/icons';
import useConfig from 'hooks/useConfig';

// ==============================|| PROFILE MENU ||============================== //

const ProfileSection = () => {
    const theme = useTheme();

    const accountData = useSelector((state) => state.account);
    const callStatus = useSelector((state) => state.twilioOutboundCall.status);
    const isCallActive = isCallActiveStatus(callStatus);

    const { borderRadius } = useConfig();

    const [selectedIndex] = useState(-1);
    const { logout, switchOrg } = useAuth();
    const [open, setOpen] = useState(false);
    const [switchingOrgId, setSwitchingOrgId] = useState(null);
    // Freshly fetched org memberships, so an org granted after login shows here
    // without a page refresh. Deliberately dropdown-local: nothing else renders
    // organizations, and publishing the /me payload into shared account state
    // could apply grant changes mid-workflow — that only happens on neutral
    // pages (see useFreshAccountGrants). Switching is still safe on a stale
    // list: the server re-validates membership and the switch full-page reloads.
    const [freshOrganizations, setFreshOrganizations] = useState(null);
    /**
     * anchorRef is used on different components and specifying one type leads to other components throwing an error
     * */
    const anchorRef = useRef(null);

    // Memberships other than the active org, each rendered as a switch entry.
    // Deliberately not gated on the list length: if the active org ever drops
    // out of the memberships list (access revoked while the session lives),
    // the remaining orgs must still render so the user can escape rather than
    // being stranded in an org they can no longer use.
    const organizations = freshOrganizations || accountData.organizations || [];
    const activeOrgId = accountData.organizationMeta?.meta?.id;
    const otherOrganizations = organizations.filter((org) => org.id !== activeOrgId);

    const handleLogout = async () => {
        try {
            await logout();
        } catch (err) {
            logger.error('account:logout failed', { error: err?.message, code: err?.code });
        }
    };

    // switchOrg persists the new session and full-page reloads; the switching
    // state only needs clearing on failure.
    const handleSwitchOrg = async (orgId) => {
        if (switchingOrgId) {
            return;
        }
        setSwitchingOrgId(orgId);
        try {
            await switchOrg(orgId);
        } catch (err) {
            logger.warn('account:switch-org failed', { error: err?.message, code: err?.code, orgId });
            setSwitchingOrgId(null);
        }
    };

    const handleClose = (event) => {
        if (anchorRef.current && anchorRef.current.contains(event.target)) {
            return;
        }
        setOpen(false);
    };
    // const handleListItemClick = (event, index, route = '') => {
    //     setSelectedIndex(index);
    //     handleClose(event);

    //     if (route && route !== '') {
    //         navigate(route);
    //     }
    // };
    const handleToggle = () => {
        if (isCallActive) {
            return;
        }
        setOpen((prevOpen) => !prevOpen);
    };

    // Refresh the org list on every open — /api/account/me is redis-cached
    // server-side and the dropdown is rarely opened, so no staleness gate. On
    // failure keep whatever list is already rendered (redux, or a prior fetch).
    useEffect(() => {
        if (!open) {
            return undefined;
        }
        let active = true;
        fetchAccountOrganizations()
            .then((orgList) => {
                if (active) {
                    setFreshOrganizations(orgList);
                }
            })
            .catch((err) => {
                logger.warn('account:profile-dropdown organizations refresh failed', { error: err?.message, code: err?.code });
            });
        return () => {
            active = false;
        };
    }, [open]);

    const prevOpen = useRef(open);
    useEffect(() => {
        if (prevOpen.current === true && open === false) {
            anchorRef.current.focus();
        }

        prevOpen.current = open;
    }, [open]);

    return (
        <>
            <Chip
                sx={{
                    height: '48px',
                    alignItems: 'center',
                    borderRadius: '27px',
                    transition: 'all .2s ease-in-out',
                    borderColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    backgroundColor: theme.palette.mode === 'dark' ? theme.palette.dark.main : theme.palette.primary.light,
                    '&[aria-controls="menu-list-grow"], &:hover': {
                        borderColor: theme.palette.primary.main,
                        background: `${theme.palette.primary.main}!important`,
                        color: theme.palette.primary.light,
                        '& svg': {
                            stroke: theme.palette.primary.light
                        }
                    },
                    '& .MuiChip-label': {
                        lineHeight: 0
                    }
                }}
                icon={
                    <Avatar
                        src={User1}
                        sx={{
                            ...theme.typography.mediumAvatar,
                            margin: '8px 0 8px 8px !important',
                            cursor: 'pointer'
                        }}
                        ref={anchorRef}
                        aria-controls={open ? 'menu-list-grow' : undefined}
                        aria-haspopup="true"
                        color="inherit"
                    />
                }
                label={<IconSettings stroke={1.5} size="1.5rem" color={theme.palette.primary.main} />}
                variant="outlined"
                ref={anchorRef}
                aria-controls={open ? 'menu-list-grow' : undefined}
                aria-haspopup="true"
                onClick={handleToggle}
                disabled={isCallActive}
                color="primary"
            />

            <Popper
                placement="bottom"
                open={open && !isCallActive}
                anchorEl={anchorRef.current}
                role={undefined}
                transition
                disablePortal
                popperOptions={{
                    modifiers: [
                        {
                            name: 'offset',
                            options: {
                                offset: [0, 14]
                            }
                        }
                    ]
                }}
            >
                {({ TransitionProps }) => (
                    <ClickAwayListener onClickAway={handleClose}>
                        <Transitions in={open} {...TransitionProps}>
                            <Paper>
                                {open && (
                                    <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                                        <Box sx={{ p: 2, pb: 0 }}>
                                            <Stack>
                                                <Typography variant="subtitle2">Logged in as</Typography>
                                                <Stack direction="row" spacing={0.5} alignItems="center">
                                                    <Typography variant="h5">{accountData.email}</Typography>
                                                </Stack>
                                                {accountData.organizationMeta?.meta?.name && (
                                                    <Typography variant="caption" color="textSecondary">
                                                        {accountData.organizationMeta.meta.name}
                                                    </Typography>
                                                )}
                                            </Stack>
                                        </Box>
                                        {/* <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 250px)', overflowX: 'hidden' }}> */}
                                        <Box sx={{ p: 2, pt: 0 }}>
                                            {/* <Card
                                                sx={{
                                                    bgcolor:
                                                        theme.palette.mode === 'dark'
                                                            ? theme.palette.dark[800]
                                                            : theme.palette.primary.light,
                                                    my: 2
                                                }}
                                            /> */}
                                            <List
                                                component="nav"
                                                sx={{
                                                    width: '100%',
                                                    maxWidth: 350,
                                                    minWidth: 300,
                                                    backgroundColor: theme.palette.background.paper,
                                                    borderRadius: '10px',
                                                    [theme.breakpoints.down('md')]: {
                                                        minWidth: '100%'
                                                    },
                                                    '& .MuiListItemButton-root': {
                                                        mt: 0.5
                                                    }
                                                }}
                                            >
                                                {/* <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={selectedIndex === 0}
                                                        onClick={(event) => handleListItemClick(event, 0, '/user/account-profile/profile1')}
                                                    >
                                                        <ListItemIcon>
                                                            <IconSettings stroke={1.5} size="1.3rem" />
                                                        </ListItemIcon>
                                                        <ListItemText primary={<Typography variant="body2">Account Settings</Typography>} />
                                                    </ListItemButton>
                                                    <ListItemButton
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        selected={selectedIndex === 1}
                                                        onClick={(event) => handleListItemClick(event, 1, '/user/social-profile/posts')}
                                                    >
                                                        <ListItemIcon>
                                                            <IconUser stroke={1.5} size="1.3rem" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={
                                                                <Grid container spacing={1} justifyContent="space-between">
                                                                    <Grid item>
                                                                        <Typography variant="body2">Placeholder</Typography>
                                                                    </Grid>
                                                                    <Grid item>
                                                                        <Chip
                                                                            label="02"
                                                                            size="small"
                                                                            sx={{
                                                                                bgcolor:
                                                                                    theme.palette.mode === 'dark'
                                                                                        ? theme.palette.dark.dark
                                                                                        : theme.palette.warning.dark,
                                                                                color: theme.palette.background.default
                                                                            }}
                                                                        />
                                                                    </Grid>
                                                                </Grid>
                                                            }
                                                        />
                                                    </ListItemButton> */}
                                                <ListItemButton
                                                    component={Link}
                                                    to={`/user/${accountData.userId}/detail`}
                                                    onClick={() => {
                                                        setOpen(false);
                                                    }}
                                                    sx={{ borderRadius: `${borderRadius}px` }}
                                                >
                                                    <ListItemIcon>
                                                        <IconSettings stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Profile</Typography>} />
                                                </ListItemButton>
                                                {otherOrganizations.map((org) => (
                                                    <ListItemButton
                                                        key={org.id}
                                                        sx={{ borderRadius: `${borderRadius}px` }}
                                                        disabled={isCallActive || !!switchingOrgId}
                                                        onClick={() => handleSwitchOrg(org.id)}
                                                    >
                                                        <ListItemIcon>
                                                            <IconSwitchHorizontal stroke={1.5} size="1.3rem" />
                                                        </ListItemIcon>
                                                        <ListItemText
                                                            primary={<Typography variant="body2">{`Switch to ${org.name}`}</Typography>}
                                                        />
                                                    </ListItemButton>
                                                ))}
                                                <ListItemButton
                                                    sx={{ borderRadius: `${borderRadius}px` }}
                                                    selected={selectedIndex === 4}
                                                    onClick={handleLogout}
                                                >
                                                    <ListItemIcon>
                                                        <IconLogout stroke={1.5} size="1.3rem" />
                                                    </ListItemIcon>
                                                    <ListItemText primary={<Typography variant="body2">Logout</Typography>} />
                                                </ListItemButton>
                                            </List>
                                        </Box>
                                        {/* </PerfectScrollbar> */}
                                    </MainCard>
                                )}
                            </Paper>
                        </Transitions>
                    </ClickAwayListener>
                )}
            </Popper>
        </>
    );
};

export default ProfileSection;
